import { useLayoutEffect, useRef, type RefObject } from 'react';

type Position = { top: number; anchor?: string; offset?: number; pages: number };
const positions = new Map<string, Position>();
export function resetCollectionPosition(key: string) {
  positions.delete(key);
  try {
    sessionStorage.removeItem(key);
  } catch {}
}
function read(key: string): Position | undefined {
  if (positions.has(key)) return positions.get(key);
  try {
    const value = JSON.parse(sessionStorage.getItem(key) || 'null');
    if (value && Number.isFinite(value.top) && Number.isInteger(value.pages)) return value;
  } catch {}
}
// Collection scroll belongs to this element, not window. Retain pagination and
// a visible item anchor so returning from a reader survives changed card heights.
export function useCollectionPosition({
  key,
  ref,
  pages,
  ready,
  hidden,
  hasNextPage,
  fetching,
  fetchNextPage,
}: {
  key: string;
  ref: RefObject<HTMLDivElement | null>;
  pages: number;
  ready: boolean;
  hidden: boolean;
  hasNextPage: boolean;
  fetching: boolean;
  fetchNextPage: () => unknown;
}) {
  const pending = useRef<Position | undefined>(undefined);
  const saveRef = useRef<() => void>(() => {});
  const latest = useRef({ pages, hidden });
  latest.current = { pages, hidden };
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    pending.current = read(key) || { top: 0, pages: 1 };
    const save = () => {
      if (pending.current || latest.current.hidden || !element.getClientRects().length) return;
      const bounds = element.getBoundingClientRect();
      const anchor = [...element.querySelectorAll<HTMLElement>('[data-item-id]')].find(
        (entry) => entry.getBoundingClientRect().bottom > bounds.top + 1,
      );
      const value: Position = {
        top: element.scrollTop,
        pages: Math.max(1, latest.current.pages),
        anchor: anchor?.dataset.itemId,
        offset: anchor ? anchor.getBoundingClientRect().top - bounds.top : undefined,
      };
      positions.set(key, value);
      try {
        sessionStorage.setItem(key, JSON.stringify(value));
      } catch {}
    };
    saveRef.current = save;
    element.addEventListener('scroll', save, { passive: true });
    window.addEventListener('pagehide', save);
    return () => {
      element.removeEventListener('scroll', save);
      window.removeEventListener('pagehide', save);
    };
  }, [key, ref]);
  useLayoutEffect(() => {
    saveRef.current();
  }, [pages]);
  useLayoutEffect(() => {
    // Mobile hides the collection while reading; reapply its saved offset when
    // shown again, after the display and asynchronous page commits are complete.
    if (!hidden && !pending.current) pending.current = read(key);
  }, [hidden, key]);
  useLayoutEffect(() => {
    const element = ref.current;
    const target = pending.current;
    if (!element || !ready || hidden || !target) return;
    if (pages < target.pages && hasNextPage) {
      if (!fetching) void fetchNextPage();
      return;
    }
    let frame = 0;
    let remaining = 6;
    const restore = () => {
      element.scrollTop = target.top;
      const anchor = target.anchor
        ? [...element.querySelectorAll<HTMLElement>('[data-item-id]')].find(
            (entry) => entry.dataset.itemId === target.anchor,
          )
        : undefined;
      if (anchor && target.offset !== undefined) {
        element.scrollTop +=
          anchor.getBoundingClientRect().top - element.getBoundingClientRect().top - target.offset;
      }
      if (--remaining > 0) frame = requestAnimationFrame(restore);
      else pending.current = undefined;
    };
    frame = requestAnimationFrame(restore);
    return () => cancelAnimationFrame(frame);
  }, [key, ref, pages, ready, hidden, hasNextPage, fetching, fetchNextPage]);
}
